import { useState, useEffect } from "react";

import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Tab from "react-bootstrap/Tab";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Alert, LoadingSpinner, InputField, SelectField, Button } from "@evos/components";

import { zodChangeConfigFormArguments } from "types";
import { isEmpty } from "lib/functions/general";
import {
  CHGR_MODE_OPTIONS,
  normalizeChargerConfigState,
  normalizeChargerConfigValue,
} from "lib/functions/charger";

const SELECT_OPTIONS_BY_KEY: Record<string, readonly string[]> = {
  chgrMode: CHGR_MODE_OPTIONS,
};

type ChangeConfigModalProps = {
  showChangeConfigModal: boolean;
  setShowChangeConfigModal: (show: boolean) => void
  serialSelected: string;
  currentConfigState: Record<string, string>;
  getConfigStatus: "success" | "error" | "loading" | "idle";
  submitSuccess: Array<{configItem: string} | null>
  submitErrors: Array<{configItem: string; message?: string} | null>
  submitPending: Array<{configItem: string} | null>
  submitLoading: boolean
  handleSetCurrentConfigState: (serial?: string) => void;
  onConfigFormSubmit: (formData: Record<string, string>) => void;
};

export default function ChangeConfigModal({
  showChangeConfigModal, 
  setShowChangeConfigModal, 
  serialSelected,
  currentConfigState,
  getConfigStatus,
  submitSuccess,
  submitErrors,
  submitPending,
  submitLoading,
  handleSetCurrentConfigState,
  onConfigFormSubmit
}: ChangeConfigModalProps) {

  const [ configModalConfirmScreen, changeConfigModalConfirmScreen ] = useState(false);

  const {
    register: registerConfig,
    // unregister: unregisterConfig,
    getValues: getConfigValues,
    handleSubmit: handleConfigSubmit,
    reset: resetConfigForm,
    getFieldState,
    formState: {
      isDirty,
      errors,
      isSubmitted
    }
  } = useForm({
    defaultValues: normalizeChargerConfigState(currentConfigState),
    resolver: zodResolver(zodChangeConfigFormArguments)
  });

  const handleReview = handleConfigSubmit(() => {
    changeConfigModalConfirmScreen(true);
  });

  // useEffect(() => {
  //   // Unregister form fields when they are deselected
  //   if (Array.isArray(serialsSelected) && getConfigValues() != null && !isEmpty(getConfigValues())) {
  //     if (Object.keys(getConfigValues()).filter((key) => serialsSelected.some((serial) => key.includes(serial))).length === 0) {
  //       Object.keys(getConfigValues()).forEach((key) => unregisterConfig(key));
  //     } 
  //   }
  // }, [getConfigValues, serialsSelected, unregisterConfig]);

  useEffect(() => {
    if (!isEmpty(currentConfigState) && Object.values(getConfigValues()).every(val => val === "")) {
      resetConfigForm(normalizeChargerConfigState(currentConfigState));
    }
  }, [currentConfigState, getConfigValues, resetConfigForm, showChangeConfigModal]);
  
  return (
    <div id="ChangeConfigModal">
      <Modal 
        show={showChangeConfigModal} 
        onHide={() => {
          setShowChangeConfigModal(false);
          resetConfigForm();
          if (configModalConfirmScreen) {
            changeConfigModalConfirmScreen(false);
          }
        }}
        onShow={() => {
          handleSetCurrentConfigState(serialSelected);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Set Configuration</Modal.Title>
        </Modal.Header>
        <Form>
          <Tab.Container 
            defaultActiveKey="set" 
            activeKey={(submitSuccess.length > 0 || submitErrors.length > 0 || submitPending.length > 0)
              ? "messages"
              : configModalConfirmScreen
                ? "review"
                : "set"
            }
          >
            <Tab.Content>
              <Tab.Pane eventKey="set">
                <Modal.Body>
                  <h5 className="mb-3">Config Items to Update</h5>
                  { getConfigStatus === "error" 
                    ? <Alert variant="danger" message="There was an error gettin the charger config. Please try again." />
                    : getConfigStatus === "loading"
                      ? <LoadingSpinner />
                      : Object.entries(currentConfigState)?.map(([configKey, configVal]) => {
                        const selectOptions = SELECT_OPTIONS_BY_KEY[configKey];
                        const normalizedValue = normalizeChargerConfigValue(configKey, configVal);
                        if (selectOptions != null) {
                          const optionValues = selectOptions.includes(normalizedValue)
                            ? selectOptions
                            : normalizedValue.length > 0
                              ? [normalizedValue, ...selectOptions]
                              : selectOptions;
                          return (
                            <SelectField
                              key={configKey}
                              name={configKey}
                              label={configKey}
                              options={[
                                { value: "", label: `Select ${configKey}`, disabled: true },
                                ...optionValues.map((option) => ({ value: option, label: option })),
                              ]}
                              register={registerConfig}
                              error={errors[configKey]}
                              isSubmitted={isSubmitted}
                              getFieldState={getFieldState}
                            />
                          );
                        }
                        return (
                          <InputField
                            key={configKey}
                            name={configKey}
                            label={configKey}
                            type="text"
                            register={registerConfig}
                            defaultValue={configVal}
                            error={errors[configKey]}
                            isSubmitted={isSubmitted}
                            getFieldState={getFieldState}
                          />
                        );
                      })
                  }
                </Modal.Body>
                <Modal.Footer>
                  <Button 
                    variant="secondary" 
                    onClick={() => {
                      setShowChangeConfigModal(false);
                      handleSetCurrentConfigState();
                      resetConfigForm();
                    }}
                  >Cancel
                  </Button>
                  <Button 
                    variant="primary" 
                    type="button"
                    disabled={serialSelected == null || serialSelected?.length === 0 || !isDirty}
                    onClick={handleReview}
                  >Review Changes
                  </Button>
                </Modal.Footer>
              </Tab.Pane>
              <Tab.Pane eventKey="review">
                <Modal.Body>
                  <h5 className="mb-3">Review Changes:</h5>
                  <div>
                    <p className="fw-semibold">{serialSelected}</p>
                    <ul>
                      {Object.keys(currentConfigState).map(key => 
                        normalizeChargerConfigValue(key, currentConfigState[key]) !== getConfigValues()[key] && (
                          <li key={key}>
                            <b>{key}</b>: <s>{normalizeChargerConfigValue(key, currentConfigState[key])}</s> &#8594; {getConfigValues()[key]}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                </Modal.Body>
                <Modal.Footer>
                  <Button 
                    variant="secondary" 
                    onClick={() => changeConfigModalConfirmScreen(false) }
                  >
                    Go Back
                  </Button>
                  <Button 
                    variant="primary" 
                    isLoading={submitLoading}
                    onClick={() => onConfigFormSubmit(getConfigValues())}
                  >
                    Apply Changes
                  </Button>
                </Modal.Footer>
              </Tab.Pane>
              <Tab.Pane eventKey="messages">
                <Modal.Body>
                  { submitSuccess != null && submitSuccess.length > 0
                    && <Alert
                      variant="success"
                      className="pb-0"
                      message={
                        <ul>
                          { submitSuccess.map((success) => (
                            success != null 
                              && <li key={success.configItem}>
                                Successfully set <b>{success.configItem}</b>
                              </li>
                          ))}
                        </ul>
                      }
                    />
                  }
                  { submitPending != null && submitPending.length > 0
                    && <Alert
                      variant="info"
                      className="pb-0"
                      message={
                        <ul>
                          { submitPending.map((pending) => (
                            pending != null 
                              && <li key={pending.configItem}>
                                Pending status set for <b>{pending.configItem}</b>
                              </li>
                          ))}
                        </ul>
                      }
                    />
                  }
                  { submitErrors != null && submitErrors.length > 0
                    && <Alert 
                      variant="danger" 
                      className="pb-0"
                      message={
                        <ul>
                          { submitErrors.map((error) => (
                            error != null 
                              && <li key={error.configItem}>
                                Error setting <b>{error.configItem}</b>
                                { error.message != null && error.message.length > 0 && <> – {error.message}</> }
                              </li>
                          ))}
                        </ul>
                      }
                    />
                  }
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={() => {
                    setShowChangeConfigModal(false);
                    resetConfigForm();
                    if (configModalConfirmScreen) {
                      changeConfigModalConfirmScreen(false);
                    }
                  }}
                  >
                    Close
                  </Button>
                </Modal.Footer>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Form>
      </Modal>
    </div>
  );
}
